import { useRevalidator } from "react-router-dom";

import { fetchRequired } from "data/keystone-api-client";
import { KEYSTONE } from "data/ss-data-store";
import {
  type KeystonePolicyInfo,
  type KeystoneUser,
  type PolicyDetail,
  authUser,
  getCookie,
  isLoggedIn,
  logException,
} from "utils";

export type KeystoneCache = {
  user: KeystoneUser | null;
  policies: KeystonePolicyInfo[] | null;
  policy: KeystonePolicyInfo | null;
  policyDetails: PolicyDetail | null;
};

let cache: KeystoneCache = {
  user: null,
  policies: null,
  policy: null,
  policyDetails: null,
};

export async function keystoneAppData() {
  const email = authUser()?.email;
  if (!isLoggedIn() || !email) {
    return null;
  }

  if (cache.user && cache.policies && cache.policy) {
    return cache;
  }

  const userAndPolicies = await fetchRequired(
    KEYSTONE.getUser(email),
    KEYSTONE.getPoliciesList(email)
  );
  if (userAndPolicies) {
    const [user, policies] = userAndPolicies;
    const selectedPolicy = getSelectedPolicy(policies);
    const policyDetailsAndCarrier = await fetchRequired(
      KEYSTONE.getPolicy(selectedPolicy.policyId)
    );
    if (policyDetailsAndCarrier) {
      const [policyDetails] = policyDetailsAndCarrier;
      cache.policy = selectedPolicy;
      cache.policyDetails = policyDetails;
      cache.user = user;
      cache.policies = policies;
      return cache;
    }
  }

  return null;
}
export function useRefreshKeystoneAppData() {
  const revalidator = useRevalidator();
  return () => {
    cache = {
      user: null,
      policies: null,
      policy: null,
      policyDetails: null,
    };
    revalidator.revalidate();
  };
}

/**
 * Check if policy selection state is stored in a cookie. If not return the first policy.
 */
function getSelectedPolicy(list: KeystonePolicyInfo[]) {
  const policyIdCookie = getCookie("ss-policy-id");
  if (policyIdCookie) {
    try {
      const selected = list.find(
        (policy) => policy.policyId === policyIdCookie
      );
      if (selected) {
        return selected;
      }
    } catch (err) {
      // We'll ignore this error and just try to set the default below
      logException(err);
    }
  }
  const defaultPolicy = list[0];
  if (defaultPolicy) {
    return defaultPolicy;
  }
  throw new Error("no default policy to set");
}
